/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useCallback, useContext, useState } from 'react'
import { useServiceContext } from '../../../root'
import { RcmTableDataWrapper } from '../../../components/rcm-table/RcmTableDataWrapper'
import RcmTableFilterTrigger from '../../../components/rcm-table/RcmTableFilterTrigger'
import Loader from '../../../components/shared/Loader'
import PageMenu from './PageMenu'
import TableFilter from './TableFilter'
import Table from './Table'
import {
  FolderStructureTemplateType,
  folderStructureTemplateTypesApiService
} from '../../../services/api/api.folderstructuretemplatetypes'
import ModalEdit from './Actions/ModalEdit'
import { RcmTableDataContext } from '../../../components/rcm-table/RcmTableDataContext'

const FolderStructureTemplateTypesContent: React.FC = () => {
  const tableContext = useContext(RcmTableDataContext)
  const context = useServiceContext()
  const { translate } = context.service.filter
  const [editingFolderStrctTplType, setEditingFolderStrctTplType] = useState<FolderStructureTemplateType | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const handleOpenModal = (folderStrctTplType?: FolderStructureTemplateType) => {
    setEditingFolderStrctTplType(folderStrctTplType || null)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingFolderStrctTplType(null)
  }

  return (
    <>
      <Loader blocking={isExporting}>
        <div className="row">
          <div className="col-xs-12">
            <div className="d-flex flex-wrap" style={{ paddingBottom: '1%' }}>
              <h1 className="align-left align-top" id="ea3caf4b-20a5-427b-b582-cde94b429258" style={{ margin: '0' }}>
                {translate('FOLDER_STRUCTURE_TEMPLATE_TYPES_TITLE')}
              </h1>

              <div className="align-right align-bottom d-flex">
                <PageMenu
                  onAddFolderStrctTplType={() => handleOpenModal()}
                  onExportStart={() => setIsExporting(true)}
                  onExportEnd={() => setIsExporting(false)}
                />
                <RcmTableFilterTrigger />
              </div>
            </div>

            <TableFilter />
            <Table
              onEditFolderStrctTplType={handleOpenModal}
              onDeleteSuccess={() => tableContext?.refreshTable()}
              onExportStart={() => setIsExporting(true)}
              onExportEnd={() => setIsExporting(false)}
            />
          </div>
        </div>
        {isModalOpen && (
          <ModalEdit folderStrctTplType={editingFolderStrctTplType || undefined} onClose={handleCloseModal} />
        )}
      </Loader>
    </>
  )
}

const FolderStructureTemplateTypesPage: React.FC = () => {
  // Create loadData function for RcmTableDataWrapper
  const loadData = useCallback(async (pagingParams: any, filter: any) => {
    console.log('Load folder structure template types data with filter:', filter)
    try {
      // Extract IDs from filter.types array - each item has structure { value: id, label: name }
      const typeIds = filter?.types && filter.types.length > 0 ? filter.types.map((t: any) => t.value || t.id || t) : []

      const params = {
        pageStart: pagingParams.pageStart || 0,
        pageSize: pagingParams.pageSize || 15,
        sortBy: pagingParams.sortBy || 'NAME',
        sortDesc: pagingParams.sortDesc !== undefined ? pagingParams.sortDesc : false,
        ...(typeIds.length > 0 ? { id: typeIds } : {})
      }

      const result: any = await folderStructureTemplateTypesApiService.getAllForTable(params)

      return {
        elements: result.elements || [],
        totalElements: result.totalElements || 0
      }
    } catch (error) {
      console.error('Error loading folder structure template types:', error)
      return {
        elements: [],
        totalElements: 0
      }
    }
  }, [])

  return (
    <div className="mcc-content-frame theme-sandman">
      <RcmTableDataWrapper
        loadData={loadData}
        defaultSortBy="NAME"
        defaultSortDesc={false}
        persistenceKey="settingsFolderStructureTemplateTypes"
        doPreload={true}
      >
        <FolderStructureTemplateTypesContent />
      </RcmTableDataWrapper>
    </div>
  )
}

export default FolderStructureTemplateTypesPage
